/**
 * useLanguage Hook
 * Manages language state and i18n integration
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '@modules/auth';
import { setActiveLanguage } from '@/utils/locale';

type Language = 'en' | 'he';
type Direction = 'ltr' | 'rtl';

interface UseLanguageReturn {
  language: Language;
  direction: Direction;
  isHebrew: boolean;
  isEnglish: boolean;
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
  t: (key: string, options?: any) => string;
}

export function useLanguage(): UseLanguageReturn {
  const { i18n, t } = useTranslation();
  const [language, setLanguageState] = useState<Language>(() => {
    // Initialize from localStorage or i18n current language
    const storedLang = authService.getLanguage();
    return storedLang;
  });

  // Update i18n, localStorage, and document attribute when language changes
  useEffect(() => {
    // Only when it is actually a DIFFERENT language. `changeLanguage` emits
    // `languageChanged` unconditionally, and react-i18next answers that event by
    // handing every subscribed component a brand-new `t` function. This hook is
    // used by nearly every component, so mounting one — a drawer, a tooltip, a
    // table cell — used to fire the event and give the whole tree a new `t`.
    // Anything with `t` in a useCallback/useEffect dependency list then re-ran:
    // in the Library that meant clicking a folder refetched the folder tree and
    // tore the navigator down to a spinner.
    if (i18n.language !== language) i18n.changeLanguage(language);
    authService.setLanguage(language);

    // Keep date formatting in step with the UI language. Without this the
    // months stay English on a Hebrew page.
    setActiveLanguage(language);

    const direction: Direction = language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
  }, [language, i18n]);

  const toggleLanguage = useCallback(() => {
    setLanguageState((prev) => (prev === 'en' ? 'he' : 'en'));
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const direction: Direction = language === 'he' ? 'rtl' : 'ltr';

  return {
    language,
    direction,
    isHebrew: language === 'he',
    isEnglish: language === 'en',
    toggleLanguage,
    setLanguage,
    t,
  };
}
